import { getLogger } from '@logtape/logtape';
import { createElement } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { CollectionSuccessNotification } from '@/components/collection-success-notification.tsx';

import { useAppointmentChainConfigs } from '@/hooks/admin/use-appointment-chain-configs.ts';
import { revalidateAppointmentsRangeCache } from '@/hooks/use-appointments-for-date-range.ts';
import useConfig from '@/hooks/useConfig.ts';
import { useEnrichedPractitionerData } from '@/hooks/useEnrichedPractitionerData.ts';

import { ApiError } from '@/lib/api-client.ts';

import { NotificationType } from '@/models/Notification.ts';
import { CollectionStatus, CollectionType, Priority } from '@/models/appointment/Appointment.ts';
import {
    type ChainAvailabilityItem,
    ChainAvailabilityStatus,
} from '@/models/appointment/AppointmentChainAvailability.ts';
import type { AppointmentChainConfig, ChainStep } from '@/models/appointment/AppointmentChainConfig.ts';
import type {
    EnrichedAppointmentType,
    EnrichedPractitionerData,
    Practice,
    Practitioner,
} from '@/models/appointment/EnrichedPractitionerData.ts';
import type {
    BookAppointmentChainDto,
    ChainCollectionAppointmentEntry,
} from '@/models/appointment/book-appointment-collection-dto.ts';
import { PVSProvider } from '@/models/customers/PVSProvider.ts';
import { InsuranceType } from '@/models/insurance.ts';

import {
    SchedulerInsuranceInfoSchema,
    ValidateUserInsuranceResponse,
} from '@/schemas/validate-insurance-response.schema.ts';

import { bookAppointmentChain } from '@/services/appointmentService.ts';
import {
    hideNotification,
    showErrorNotification,
    showLoadingNotification,
    showWarningNotification,
    updateNotification,
} from '@/services/notificationService.ts';
import { validateInsuranceUser } from '@/services/patientService.ts';

import { useBookingStore } from '@/stores/bookingStore.ts';

import { extractApiErrorMessage } from '@/utils/api-error-utils.ts';
import { resolveBookingErrorMessage } from '@/utils/booking-error-utils.ts';
import { getActiveMarkedSteps } from '@/utils/chain-main-practitioner-utils.ts';
import { buildChainSummaryEntries, pluralizeTermine } from '@/utils/collection-utils.ts';
import { INSURANCE_TYPE_TO_INSURANCE } from '@/utils/insurance-utils.ts';
import { resolvePatientUserId, resolveUserId } from '@/utils/patientUtils.ts';
import {
    findAppointmentTypeInInstance,
    findPractitionerInInstanceByCalendar,
} from '@/utils/practitioner/bookingFormOptionsUtils.ts';
import { getProfessionalTypeKey, getTextFromRichTextField } from '@/utils/prismicUtils.ts';

const logger = getLogger(['hook', 'book-appointment-chain']);

interface BookChainResult {
    success: boolean;
    needsDuplication?: boolean;
}

interface UseBookAppointmentChainResult {
    bookChain: () => Promise<BookChainResult>;
}

interface BuildEntryParams {
    orderId: number;
    item: ChainAvailabilityItem;
    calendarId: string;
    practitioner: Practitioner;
    aptType: EnrichedAppointmentType;
    locationKey: string;
    pvsPatientId: string;
    isEternoUser: boolean;
}

const buildAppointmentEntry = (params: BuildEntryParams): ChainCollectionAppointmentEntry => {
    const { orderId, item, calendarId, practitioner, aptType, locationKey, pvsPatientId, isEternoUser } = params;
    return {
        order_id: orderId,
        professional: {
            id: practitioner.id,
            key: getTextFromRichTextField(practitioner.prismic_profile?.data.key) || '',
            type_key: getProfessionalTypeKey(practitioner.prismic_professional_type),
            location_key: locationKey,
        },
        appointment: {
            types: [{ id: aptType.id, key: item.appointment_type_key, name: aptType.name, is_mapped: true }],
            is_eterno_user: isEternoUser,
            is_at_location: aptType.is_at_location,
            is_online: aptType.is_online,
            slot: item.start_date,
            duration: { value: aptType.duration.value, units: 'MINUTES' },
            priority: Priority.NORMAL,
        },
        pvs: {
            pvs_provider: practitioner.pvs_provider,
            ...(practitioner.pvs_provider === PVSProvider.DOC_CIRRUS && {
                doc_cirrus: {
                    patient_id: pvsPatientId,
                    professional_id: practitioner.pvs.practitioner_id,
                    schedule_type_id: aptType.pvs.pvs_schedule_type_id,
                    calendar_id: calendarId,
                },
            }),
            ...(practitioner.pvs_provider === PVSProvider.DATA_CUR && {
                data_cur: {
                    patient_id: pvsPatientId,
                    professional_id: practitioner.pvs.practitioner_id,
                    event_type_id: aptType.pvs.pvs_schedule_type_id,
                    event_type_code: aptType.pvs.pvs_event_type_code ?? '',
                    resource_id: calendarId,
                },
            }),
        },
    };
};

const insuranceFlags = (
    insuranceType: InsuranceType
): Pick<BookAppointmentChainDto['patient'], 'is_public_insurance' | 'is_self_payer' | 'is_private_insurance'> => ({
    is_public_insurance: insuranceType === InsuranceType.PUBLIC,
    is_self_payer: insuranceType === InsuranceType.SELF_PAYER,
    is_private_insurance: insuranceType === InsuranceType.PRIVATE,
});

interface ResolvedStep {
    item: ChainAvailabilityItem;
    calendarId: string;
    practitioner: Practitioner;
    aptType: EnrichedAppointmentType;
    practice: Practice;
}

interface ResolveStepsParams {
    visibleSteps: ChainStep[];
    practitionerData: EnrichedPractitionerData[];
    availabilityByOrder: Map<number, ChainAvailabilityItem>;
    calendarSelections: Record<number, string>;
    instanceId: string;
    selectedLocationKey: string;
}

type ResolveStepsResult = { ok: true; steps: ResolvedStep[] } | { ok: false; reason: string };

const resolveSteps = (params: ResolveStepsParams): ResolveStepsResult => {
    const { visibleSteps, practitionerData, availabilityByOrder, calendarSelections, instanceId, selectedLocationKey } =
        params;
    const resolved: ResolvedStep[] = [];
    for (const step of visibleSteps) {
        const item = availabilityByOrder.get(step.order);
        if (!item) return { ok: false, reason: 'Fehlende Verfügbarkeit für mindestens einen Schritt.' };
        if (item.status !== ChainAvailabilityStatus.AVAILABLE) {
            return { ok: false, reason: 'Mindestens ein Schritt ist nicht mehr verfügbar.' };
        }
        const calendarId = calendarSelections[step.order] ?? item.calendar_id;
        const aptType = findAppointmentTypeInInstance(practitionerData, instanceId, step.appointment_type_id);
        if (!aptType) return { ok: false, reason: 'Terminart nicht gefunden. Praktizierende Daten neu laden.' };
        const practitioner = findPractitionerInInstanceByCalendar(practitionerData, instanceId, calendarId);
        if (!practitioner) return { ok: false, reason: 'Behandler:in nicht gefunden für Kalender.' };
        const practice =
            practitioner.practices.find((p) => p.prismic_hub.key === selectedLocationKey) ?? practitioner.practices[0];
        if (!practice?.prismic_hub.key) {
            return { ok: false, reason: 'Standort nicht gefunden für Behandler:in.' };
        }
        if (practitioner.pvs_provider === PVSProvider.DOC_CIRRUS && !practice.pvs?.id) {
            return { ok: false, reason: 'Standort nicht gefunden für Behandler:in.' };
        }
        resolved.push({ item, calendarId, practitioner, aptType, practice });
    }
    return { ok: true, steps: resolved };
};

interface BuildDtoParams {
    selectedChain: AppointmentChainConfig;
    insuranceType: InsuranceType;
    resolvedSteps: ResolvedStep[];
    userId: string;
    pvsPatientId: string;
    isEternoUser: boolean;
    isReturning: boolean;
}

const buildChainDto = (params: BuildDtoParams): BookAppointmentChainDto => {
    const { selectedChain, insuranceType, resolvedSteps, userId, pvsPatientId, isEternoUser, isReturning } = params;
    const appointments: ChainCollectionAppointmentEntry[] = resolvedSteps.map((step, orderId) =>
        buildAppointmentEntry({
            orderId: orderId,
            item: step.item,
            calendarId: step.calendarId,
            practitioner: step.practitioner,
            aptType: step.aptType,
            locationKey: step.practice.prismic_hub.key,
            pvsPatientId: pvsPatientId,
            isEternoUser: isEternoUser,
        })
    );

    return {
        collection_data: {
            chain_configuration_id: selectedChain.id,
            instance_id: selectedChain.instance_id,
            booking_status: CollectionStatus.PENDING,
            type: CollectionType.CHAIN,
            chain_config: { name: selectedChain.name },
        },
        patient: {
            id: userId,
            is_eterno_user: isEternoUser,
            is_for_self: true,
            is_returning: isReturning,
            is_with_prescription: false,
            ...insuranceFlags(insuranceType),
            documents: [],
            symptoms: [],
        },
        appointments: appointments,
        is_overlap: false,
    };
};

const assertMarkedStepsMatchMainPractitioner = ({
    visibleSteps,
    resolvedSteps,
    removedOrders,
    selectedChain,
    mainPractitionerId,
}: {
    visibleSteps: ChainStep[];
    resolvedSteps: ResolvedStep[];
    removedOrders: number[];
    selectedChain: AppointmentChainConfig;
    mainPractitionerId: string | undefined;
}): boolean => {
    const markedSteps = getActiveMarkedSteps(selectedChain, removedOrders);
    if (markedSteps.length === 0) return true;
    return markedSteps.every((markedStep) => {
        const stepIndex = visibleSteps.findIndex((s) => s.order === markedStep.order);
        if (stepIndex === -1) return false;
        const resolved = resolvedSteps[stepIndex];
        return resolved?.practitioner.id === mainPractitionerId;
    });
};

export const useBookAppointmentChain = (): UseBookAppointmentChainResult => {
    const { customer_id: customerId, selected_location: selectedLocation } = useConfig();
    const { chainConfigs, isLoading: isChainsLoading } = useAppointmentChainConfigs({ customerId });
    const { data: practitionerData, is_loading: isPractitionerLoading } = useEnrichedPractitionerData();

    const {
        chain_id: chainId,
        insurance_type: insuranceType,
        removed_orders: removedOrders,
        calendar_selections: calendarSelections,
        main_practitioner_id: mainPractitionerId,
    } = useBookingStore(useShallow((state) => state.chain_selection));
    const isReturning = useBookingStore((state) => state.is_returning_patient);
    const availability = useBookingStore((state) => state.chain_availability);
    const currentPatient = useBookingStore((state) => state.current_patient);

    const bookChain = async (): Promise<BookChainResult> => {
        if (isChainsLoading || isPractitionerLoading || !practitionerData) {
            logger.warn('Booking attempted while data is still loading', {
                chainsLoading: isChainsLoading,
                practitionerLoading: isPractitionerLoading,
                hasPractitionerData: !!practitionerData,
            });
            showWarningNotification('Daten werden noch geladen. Bitte einen Moment warten.');
            return { success: false };
        }
        const selectedChain = chainConfigs.find((c) => c.id === chainId);
        if (!selectedChain || !customerId || !currentPatient || !insuranceType) {
            logger.error('Missing required data for the appointment chain', {
                hasChain: !!selectedChain,
                hasCustomerId: !!customerId,
                hasPatient: !!currentPatient,
                hasInsuranceType: !!insuranceType,
                selectedChainId: chainId,
            });
            showErrorNotification('Fehlende Daten für die Terminkette.');
            return { success: false };
        }

        const insuranceValidationUserId = resolvePatientUserId(currentPatient);
        let eternoUserId = currentPatient.eterno_user_id;
        if (!insuranceValidationUserId || !eternoUserId) {
            logger.error('Invalid patient user IDs');
            showErrorNotification('Patient hat keine gültige Benutzer-ID.');
            return { success: false };
        }

        const visibleSteps = selectedChain.steps.filter((s) => !removedOrders.includes(s.order));
        if (visibleSteps.length === 0) {
            logger.error('No visible steps after filtering removed orders', {
                totalSteps: selectedChain.steps.length,
                filteredOrders: removedOrders,
            });
            showErrorNotification('Mindestens ein Schritt erforderlich.');
            return { success: false };
        }
        const availabilityByOrder = new Map(availability.map((item) => [item.step_order, item]));

        const instanceId = selectedChain.instance_id;
        const resolveResult = resolveSteps({
            visibleSteps: visibleSteps,
            practitionerData: practitionerData,
            availabilityByOrder: availabilityByOrder,
            calendarSelections: calendarSelections,
            instanceId: instanceId,
            selectedLocationKey: selectedLocation?.key ?? '',
        });
        if (!resolveResult.ok) {
            logger.error('Step resolution failed', {
                reason: resolveResult.reason,
                chain: selectedChain.id,
                visibleStepOrders: visibleSteps.map((s) => s.order),
            });
            showErrorNotification(resolveResult.reason);
            return { success: false };
        }
        const resolvedSteps = resolveResult.steps;

        const markedStepsMatch = assertMarkedStepsMatchMainPractitioner({
            visibleSteps: visibleSteps,
            resolvedSteps: resolvedSteps,
            removedOrders: removedOrders,
            selectedChain: selectedChain,
            mainPractitionerId: mainPractitionerId,
        });
        if (!markedStepsMatch) {
            showErrorNotification('Hauptbehandler:in stimmt nicht mit den gewählten Kalendern überein.');
            return { success: false };
        }

        const chainPvsProvider = resolvedSteps[0].practitioner.pvs_provider;

        const resetBookingState = (): void => {
            useBookingStore.setState({
                is_booking_loading: false,
                isCollectionSubmitting: false,
                patient_duplication_allowed: false,
            });
        };
        useBookingStore.setState({ is_booking_loading: true, isCollectionSubmitting: true });

        let pvsPatientId = currentPatient.pvs_user_id;
        if (chainPvsProvider === PVSProvider.DOC_CIRRUS) {
            const insurancesInfos: SchedulerInsuranceInfoSchema[] = resolvedSteps.map((step) => ({
                insurance_type: insuranceType,
                location_id: step.practice.pvs?.id ?? '',
            }));
            const { patient_duplication_allowed: allowDuplication } = useBookingStore.getState();

            let insuranceResult: ValidateUserInsuranceResponse | undefined;
            let insuranceError = false;
            try {
                insuranceResult = await validateInsuranceUser(customerId, instanceId, insuranceValidationUserId, {
                    schedule_insurance_info: insurancesInfos,
                    allow_duplication: allowDuplication,
                });
            } catch (error) {
                const status = error instanceof ApiError ? error.status : undefined;
                const backendMessage = extractApiErrorMessage(error);
                logger.error('Insurance validation failed', { httpStatus: status, errorMessage: backendMessage });
                insuranceError = true;
            }
            if (insuranceError || !insuranceResult) {
                resetBookingState();
                showErrorNotification('Fehler bei der Versicherungsprüfung. Bitte versuchen Sie es erneut.');
                return { success: false };
            }
            if (insuranceResult.duplication_required && !insuranceResult.duplication_done) {
                resetBookingState();
                return { success: false, needsDuplication: true };
            }
            const dcUserId = insuranceResult.users_insurance_infos[0]?.dc_user_id?.trim() ?? '';
            if (!dcUserId) {
                resetBookingState();
                logger.error('Empty dc_user_id from insurance validation');
                showErrorNotification('Fehler bei der Ermittlung der Versicherungsdaten.');
                return { success: false };
            }
            // In case the insurance validation returns a different eterno_user_id and pvs_user_id, we should use those for the booking.
            pvsPatientId = dcUserId;
            if (insuranceResult.eterno_user_id) {
                eternoUserId = insuranceResult.eterno_user_id;
            }
        }

        const resolvedUser = resolveUserId(eternoUserId, instanceId, pvsPatientId);
        const dto = buildChainDto({
            selectedChain: selectedChain,
            insuranceType: insuranceType,
            resolvedSteps: resolvedSteps,
            // We use this method to either use the eternoUserId resolved from the insurance validation or the pvsPatientId to build the userId for the booking
            userId: resolvedUser.id,
            pvsPatientId: pvsPatientId,
            isEternoUser: resolvedUser.isEternoUser,
            isReturning: isReturning,
        });

        const stepsCount = dto.appointments.length;
        let notificationId = '';

        try {
            notificationId = showLoadingNotification(`Buche ${pluralizeTermine(stepsCount)}...`, 'Terminkette');

            const response = await bookAppointmentChain(customerId, dto.patient.id, dto);

            useBookingStore.setState({ patient_duplication_allowed: false });

            if (response.collection.booking_status === CollectionStatus.FAILED) {
                hideNotification(notificationId);
                showErrorNotification('Fehler beim Buchen der Terminkette.');
                resetBookingState();
                return { success: false };
            }

            const bookedAppointments = [...response.pre_booked_appointments].sort((a, b) =>
                a.start.localeCompare(b.start)
            );

            updateNotification(NotificationType.SUCCESS, {
                id: notificationId,
                message: createElement(CollectionSuccessNotification, { appointments: bookedAppointments }),
                title: `Terminkette erfolgreich gebucht (${pluralizeTermine(bookedAppointments.length)})`,
            });
            revalidateAppointmentsRangeCache();

            useBookingStore.getState().completeCollectionBooking(
                'chain',
                buildChainSummaryEntries({
                    appointments: bookedAppointments,
                    resolvedSteps: resolvedSteps,
                    insurance: INSURANCE_TYPE_TO_INSURANCE[insuranceType],
                    pvsProvider: chainPvsProvider,
                })
            );

            return { success: true };
        } catch (err) {
            const status = err instanceof ApiError ? err.status : undefined;
            const backendMessage = extractApiErrorMessage(err);
            logger.error('Chain booking failed', {
                httpStatus: status,
                errorMessage: backendMessage,
                chain: selectedChain.id,
                steps: stepsCount,
                customer: customerId,
            });

            if (notificationId) hideNotification(notificationId);

            const errorMsg = resolveBookingErrorMessage({
                err: err,
                defaultMessage: 'Die Terminkette konnte nicht gebucht werden. Bitte versuchen Sie es erneut.',
            });

            showErrorNotification(errorMsg);
            resetBookingState();
        }

        return { success: false };
    };

    return { bookChain };
};
